import Phaser from 'phaser'
import { useGameStore } from '@/store/gameStore'
import type { GameMode } from '@/game/types'
import { makeImageTextButton } from '@/ui/button'
import { AudioManager } from '@/services/AudioManager'
import { openSettings, closeSettings } from '@/ui/bgmPlaylist'
import { getPoints } from '@/services/shop'

export class Lobby extends Phaser.Scene {
  private nicknameInput: HTMLInputElement | null = null

  constructor() { super('Lobby') }

  create() {
    const { width, height } = this.scale

    this.add.image(width / 2, height / 2, 'bg_lobby').setDisplaySize(width, height)

    // 로고: 화면폭에 맞춤(비율 유지)
    const logo = this.add.image(width / 2, height * 0.13, 'logo').setOrigin(0.5)
    const logoW = Math.min(width * 0.84, logo.width)
    logo.setDisplaySize(logoW, logoW * (logo.height / logo.width))

    // 마스코트
    const mascot = this.add.image(width / 2, height * 0.31, 'mascot_main').setOrigin(0.5)
    const mW = Math.min(width * 0.42, mascot.width)
    mascot.setDisplaySize(mW, mW * (mascot.height / mascot.width))

    // BGM 시작 + 설정(톱니) 버튼(우상단). 효과음/BGM 볼륨·on-off + 음악 선택을 설정 패널에서.
    AudioManager.init(this)
    AudioManager.startBgm()
    const gx = width * 0.9, gy = height * 0.06, gd = width * 0.1
    if (this.textures.exists('icon_settings')) {
      this.add.image(gx, gy, 'icon_settings').setDisplaySize(gd, gd).setDepth(40)
    } else {
      const gg = this.add.graphics().setDepth(40)
      gg.fillStyle(0xffffff, 0.92); gg.fillCircle(gx, gy, gd * 0.45)
      this.add.text(gx, gy, '⚙️', { fontSize: `${Math.round(gd * 0.5)}px` }).setOrigin(0.5).setDepth(41)
    }
    this.add.zone(gx, gy, gd, gd).setInteractive({ useHandCursor: true }).setDepth(42)
      .on('pointerup', () => { AudioManager.play('ui_click', 0.3); openSettings(() => {}) })

    // 닉네임 입력(DOM 오버레이) — 이전 닉네임 유지
    const input = document.createElement('input')
    input.placeholder = '닉네임'
    input.maxLength = 20
    const saved = useGameStore.getState().nickname
    if (saved && saved !== 'Player') input.value = saved
    Object.assign(input.style, {
      position: 'absolute',
      left: '50%',
      top: '46%',
      transform: 'translate(-50%,-50%)',
      width: '62%',
      maxWidth: '320px',
      boxSizing: 'border-box',
      fontSize: '18px',
      fontWeight: '600',
      padding: '12px 16px',
      borderRadius: '16px',
      border: '3px solid #ffffff',
      outline: 'none',
      textAlign: 'center',
      color: '#3a2a6a',
      background: 'rgba(255,255,255,0.92)',
      boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
    })
    document.getElementById('game-container')!.appendChild(input)
    this.nicknameInput = input

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { this.removeInput(); closeSettings() })

    // 보유 포인트(좌상단) — 코인 이미지 + 숫자
    if (this.textures.exists('icon_point')) {
      this.add.image(width * 0.08, height * 0.06, 'icon_point').setDisplaySize(width * 0.06, width * 0.06)
    }
    this.add.text(width * 0.13, height * 0.06, `${getPoints().toLocaleString()} P`, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, color: '#ffe066', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0, 0.5)

    // 닉네임 필요한 화면 진입(챌린지 입장/연습). 닉네임 비면 경고.
    const enter = (mode: GameMode, target: string) => {
      const nick = input.value.trim()
      if (!nick) {
        this.warnNickname(input)
        return
      }
      useGameStore.getState().set({ nickname: nick, mode })
      this.removeInput()
      this.scene.start(target, { mode })
    }
    // 닉네임 저장 후 다른 화면으로(랭킹/상점)
    const goto = (key: string) => {
      const typed = input.value.trim()
      if (typed) useGameStore.getState().set({ nickname: typed })
      this.removeInput()
      this.scene.start(key)
    }

    // 2×2 그리드(작게): [입장하기][연습/튜토리얼] / [랭킹][상점]
    const bw = width * 0.45
    makeImageTextButton(this, width * 0.27, height * 0.66, 'btn_enter', '입장하기', () => enter('challenge', 'WaitingRoom'),
      { width: bw, fontSize: width * 0.037 })
    makeImageTextButton(this, width * 0.73, height * 0.66, 'btn_practice', '연습/튜토리얼', () => enter('practice', 'Game'),
      { width: bw, fontSize: width * 0.034 })
    makeImageTextButton(this, width * 0.27, height * 0.8, 'btn_rank', '랭킹', () => goto('Leaderboard'),
      { width: bw, fontSize: width * 0.04 })
    makeImageTextButton(this, width * 0.73, height * 0.8, 'btn_shop', '상점', () => goto('Shop'),
      { width: bw, fontSize: width * 0.04 })
  }

  private warnNickname(input: HTMLInputElement) {
    const { width, height } = this.scale
    // 입력폼 강조(빨간 테두리 + 흔들기)
    input.style.border = '3px solid #ff5b5b'
    input.focus()
    input.animate(
      [
        { transform: 'translate(-50%,-50%) translateX(0)' },
        { transform: 'translate(-50%,-50%) translateX(-8px)' },
        { transform: 'translate(-50%,-50%) translateX(8px)' },
        { transform: 'translate(-50%,-50%) translateX(0)' },
      ],
      { duration: 280, iterations: 1 },
    )
    setTimeout(() => { input.style.border = '3px solid #ffffff' }, 1200)
    // 토스트 메시지
    const toast = this.add
      .text(width / 2, height * 0.62, '닉네임을 입력해주세요!', {
        fontFamily: 'Arial', fontSize: `${Math.round(width * 0.05)}px`, color: '#ffffff', fontStyle: 'bold',
        backgroundColor: '#ff5b5b', padding: { x: width * 0.04, y: width * 0.02 },
      })
      .setOrigin(0.5)
      .setDepth(100)
    this.tweens.add({ targets: toast, alpha: 0, delay: 1100, duration: 500, onComplete: () => toast.destroy() })
  }

  private removeInput() {
    if (this.nicknameInput) {
      this.nicknameInput.remove()
      this.nicknameInput = null
    }
  }
}
